import { fetchMenuState } from '@/api/bffClient';
import type { SupportedLocale } from '@/types';
import type { BffMenuResponse } from '@/types/bff';
import { mapLocaleToAcceptLanguage } from '@/utils/locale';
import { useCallback, useEffect, useRef, useState } from 'react';

/** Default polling interval in milliseconds. */
export const DEFAULT_POLL_INTERVAL_MS = 10_000;

/**
 * Return type for the useBffMenu hook.
 */
export interface UseBffMenuReturn {
  data: BffMenuResponse | null;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Options for the useBffMenu hook.
 */
export interface UseBffMenuOptions {
  /** Polling interval in milliseconds. Set to `0` or `false` to disable polling. Defaults to 10000ms. */
  pollInterval?: number | false;
}

/**
 * Module-level cache for the BFF menu fetch.
 * Survives React tree unmount/remount cycles (StrictMode, Preact reconciliation,
 * custom element disconnect/reconnect) so the network request is never cancelled.
 */
const fetchCache = new Map<string, Promise<BffMenuResponse>>();

function getOrCreateFetch(acceptLanguage: string): Promise<BffMenuResponse> {
  const existing = fetchCache.get(acceptLanguage);
  if (existing) return existing;

  const promise = fetchMenuState(acceptLanguage).finally(() => {
    // Clear the cache entry once resolved/rejected so future calls re-fetch
    fetchCache.delete(acceptLanguage);
  });

  fetchCache.set(acceptLanguage, promise);
  return promise;
}

/** @internal Exposed for testing — clears the deduplication cache. */
export function _clearFetchCache(): void {
  fetchCache.clear();
}

/**
 * React hook that fetches the BFF menu state, re-fetches when locale changes,
 * and polls at a configurable interval for background updates.
 *
 * - Calls `fetchMenuState()` with the mapped Accept-Language header
 * - Re-fetches when `locale` changes
 * - Polls every `pollInterval` ms (default 10s) after initial load
 * - Only the initial fetch shows a loading state; background polls are silent
 * - On background poll failure, keeps previous `data` — no error/loading shown
 * - Guards against race conditions via a request counter (stale responses are ignored)
 * - Fetch is deduplicated at module level — survives component unmount/remount
 * - Clears polling interval on unmount
 * - Returns `{ data, isLoading, error }`
 */
export function useBffMenu(locale: SupportedLocale, options?: UseBffMenuOptions): UseBffMenuReturn {
  const [data, setData] = useState<BffMenuResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const isInitialFetch = useRef(true);

  // Counter to track the latest request — stale responses are discarded
  const requestId = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  // Locale that the current `data` was fetched for. When the requested locale
  // differs, the held data is for the wrong language and must not be shown.
  const dataLocaleRef = useRef<SupportedLocale | null>(null);

  const pollInterval =
    options?.pollInterval === false ? 0 : (options?.pollInterval ?? DEFAULT_POLL_INTERVAL_MS);

  const fetchData = useCallback(
    async (isBackground: boolean) => {
      const currentRequestId = ++requestId.current;
      const acceptLanguage = mapLocaleToAcceptLanguage(locale);

      try {
        const response = await getOrCreateFetch(acceptLanguage);

        // Only apply state if this is still the latest request and component is mounted
        if (currentRequestId !== requestId.current || !isMountedRef.current) return;

        setData(response);
        dataLocaleRef.current = locale;
        setError(null);

        if (!isBackground || isInitialFetch.current) {
          setIsLoading(false);
          isInitialFetch.current = false;
        }
      } catch (err) {
        // Ignore stale request errors or unmounted state
        if (currentRequestId !== requestId.current || !isMountedRef.current) return;

        // On background poll failure after initial load, silently keep previous data
        if (isBackground && !isInitialFetch.current) return;

        // On initial fetch failure, surface the error
        if (isInitialFetch.current) {
          setError(err instanceof Error ? err : new Error(String(err)));
          isInitialFetch.current = false;
        }
        setIsLoading(false);
      }
    },
    [locale],
  );

  // Initial fetch + start polling after it settles
  useEffect(() => {
    isMountedRef.current = true;

    // A locale change means any held `data` is for the previous language.
    // Treat it like a fresh load: clear the stale data and show loading so the
    // consumer renders its localized fallback (not stale wrong-language items)
    // if the new-locale fetch fails — e.g. offline. Without this, a failed
    // locale-change refetch would keep showing the old language.
    const localeChanged = !isInitialFetch.current && dataLocaleRef.current !== locale;

    // Show loading on the initial fetch and on a locale change.
    if (isInitialFetch.current || localeChanged) {
      setIsLoading(true);
      setError(null);
    }
    if (localeChanged) {
      setData(null);
      dataLocaleRef.current = null;
    }

    // Foreground on initial load and on locale change (so failures surface the
    // fallback); background for routine polls only.
    const isBackground = !isInitialFetch.current && !localeChanged;

    // Perform the initial/locale-change fetch — polling starts after it settles
    fetchData(isBackground).finally(() => {
      if (pollInterval > 0 && isMountedRef.current && intervalRef.current === null) {
        intervalRef.current = setInterval(() => {
          fetchData(/* isBackground */ true);
        }, pollInterval);
      }
    });

    return () => {
      isMountedRef.current = false;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fetchData, pollInterval]);

  return { data, isLoading, error };
}
